import { useMutation, useQuery } from "convex/react";
import {
	Activity,
	Cable,
	History,
	Layers3,
	Scale,
	BarChart3,
	Beaker,
	BookOpenText,
	Bot,
	Boxes,
	Calculator,
	Check,
	ChevronDown,
	ChevronRight,
	ClipboardList,
	Clock,
	CreditCard,
	DollarSign,
	FileText,
	Flame,
	Folder,
	FolderOpen,
	LogOut,
	Mail,
	Megaphone,
	MessagesSquare,
	Package,
	PanelLeft,
	Percent,
	Receipt,
	Rocket,
	ScrollText,
	Settings,
	Shield,
	Star,
	Truck,
	UserRoundCog,
	Users2,
} from "lucide-react";
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import anthemLogo from "@/assets/logo.webp";
import { LanguageSwitcher } from "@/components/language-switcher";
import { ThemeToggle } from "@/components/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { ENV } from "@/env";
import { authClient } from "@/lib/auth-client";
import { BADGE_TONES } from "@/lib/badge-tones";
import { useAppLocale } from "@/lib/i18n";
import { hasPermission } from "@/lib/roles";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { api } from "$convex/convexApi";
import {
	getActiveGroupId,
	getNavGroupsForRole,
	isNavPathActive,
	type NavGroup,
	type NavGroupId,
	type NavItem,
} from "./nav.config";

import { LogisticsReviewControl, LOGISTICS_REVIEW_PATHS, type LogisticsReviewStatus } from "./LogisticsReviewControl";

type IconComponent = React.ComponentType<{ className?: string }>;

const NAV_GROUP_ICONS: Record<NavGroupId, IconComponent> = {
	roasting: Flame,
	inventory: Boxes,
	sales: FileText,
	marketing: Megaphone,
	logistics: Truck,
	support: MessagesSquare,
	accounting: Calculator,
	admin: Settings,
};

const NAV_ITEM_ICONS: Record<string, IconComponent> = {
	"/roasting": Activity,
	"/roasting/history": History,
	"/roasting/operations": Layers3,
	"/roasting/planning": ClipboardList,
	"/roasting/yield": Scale,
	"/roasting/coffees": BookOpenText,
	"/roasting/devices": Cable,
	"/inventory/products": Package,
	"/inventory/cargos": Boxes,
	"/inventory/samples": Beaker,
	"/inventory/reservations": Clock,
	"/sales/contracts": FileText,
	"/sales/samples": Beaker,
	"/sales/leads": Users2,
	"/sales/crm": FolderOpen,
	"/sales/crm/opportunities": FolderOpen,
	"/sales/crm/leads": Users2,
	"/sales/crm/follow-ups": Clock,
	"/sales/crm/people": Users2,
	"/sales/follow-ups": Clock,
	"/sales/emails": Mail,
	"/marketing/events": Megaphone,
	"/marketing/users": Users2,
	"/marketing/emails": Mail,
	"/marketing/emails/create": Mail,
	"/sales/users": Users2,
	"/sales/discounts": Percent,
	"/logistics/orders": ClipboardList,
	"/logistics/carrier-notices": Megaphone,
	"/support": MessagesSquare,
	"/support/agents": Bot,
	"/admin?tab=inventory": Boxes,
	"/admin?tab=orders": ClipboardList,
	"/admin?tab=contracts": FileText,
	"/admin?tab=users": Users2,
	"/admin?tab=recovery": UserRoundCog,
	"/admin?tab=commissions": Percent,
	"/admin/accounting": CreditCard,
	"/admin/accounting/orders": ClipboardList,
	"/admin/accounting/cashflow": DollarSign,
	"/admin/accounting/aging": Clock,
	"/admin/accounting/plans": FileText,
	"/admin/accounting/points": Star,
	"/admin/accounting/history": Receipt,
	"/admin/accounting/audit": ScrollText,
	"/admin/analytics": BarChart3,
	"/admin/leads": Users2,
	"/admin/copilot": Bot,
	"/admin/discounts": Percent,
	"/admin/knowledgebase": BookOpenText,
	"/admin/flow": Settings,
	"/security": Shield,
};

function getDevRoleOptions() {
	return [
		{ value: "admin", label: m.role_admin() },
		{ value: "sales", label: m.role_sales() },
		{ value: "logistics", label: m.role_logistics() },
		{ value: "warehouse", label: m.role_warehouse() },
		{ value: "inventory", label: m.role_inventory() },
		{ value: "accounting", label: m.role_accounting() },
		{ value: "marketing", label: m.role_marketing() },
	] as const;
}

type DevRoleValue = ReturnType<typeof getDevRoleOptions>[number]["value"];

interface AppSidebarProps {
	user: unknown;
	userRole?: string;
	userEmail?: string | null;
	collapsed?: boolean;
	onNavigate?: () => void;
}

function getNavItemIcon(path: string, groupId: NavGroupId): IconComponent {
	return NAV_ITEM_ICONS[path] ?? NAV_GROUP_ICONS[groupId];
}

const KNOWLEDGEBASE_BASE_PATH = "/admin/knowledgebase";

type KnowledgebaseNavDocument = {
	_id: string;
	title: string;
	slug: string;
	folderPath?: string | null;
	status?: "draft" | "published" | "archived";
};

type KnowledgebaseFolderNode = {
	key: string;
	label: string;
	path: string;
	folders: KnowledgebaseFolderNode[];
	documents: KnowledgebaseNavDocument[];
};

function normalizeFolderPath(value: string | undefined | null): string {
	if (!value) return "";
	return value
		.split("/")
		.map((segment) => segment.trim())
		.filter(Boolean)
		.join("/");
}

function splitFolderPath(value: string | undefined | null): string[] {
	const normalized = normalizeFolderPath(value);
	return normalized ? normalized.split("/") : [];
}

function buildKnowledgebaseRouteKey(document: {
	folderPath?: string | null;
	slug: string;
}): string {
	const folderPath = normalizeFolderPath(document.folderPath);
	return folderPath ? `${folderPath}/${document.slug}` : document.slug;
}

function encodeRouteKey(value: string): string {
	return value
		.split("/")
		.map((segment) => encodeURIComponent(segment))
		.join("/");
}

function decodeKnowledgebaseRouteKey(pathname: string): string {
	if (pathname === KNOWLEDGEBASE_BASE_PATH) return "";
	if (!pathname.startsWith(`${KNOWLEDGEBASE_BASE_PATH}/`)) return "";
	const encoded = pathname.slice(KNOWLEDGEBASE_BASE_PATH.length + 1);
	if (!encoded) return "";
	return encoded
		.split("/")
		.map((segment) => decodeURIComponent(segment))
		.join("/");
}

function buildKnowledgebaseTree(
	documents: KnowledgebaseNavDocument[],
): KnowledgebaseFolderNode {
	const root: KnowledgebaseFolderNode = {
		key: "kb:root",
		label: "Knowledgebase",
		path: "",
		folders: [],
		documents: [],
	};

	const folderByPath = new Map<string, KnowledgebaseFolderNode>();
	folderByPath.set("", root);

	for (const document of documents) {
		const segments = splitFolderPath(document.folderPath);
		let parentPath = "";
		let parent = root;

		for (const segment of segments) {
			const currentPath = parentPath ? `${parentPath}/${segment}` : segment;
			let current = folderByPath.get(currentPath);
			if (!current) {
				current = {
					key: `kb:folder:${currentPath}`,
					label: segment,
					path: currentPath,
					folders: [],
					documents: [],
				};
				folderByPath.set(currentPath, current);
				parent.folders.push(current);
			}
			parent = current;
			parentPath = currentPath;
		}

		parent.documents.push(document);
	}

	const sortTree = (node: KnowledgebaseFolderNode) => {
		node.folders.sort((a, b) => a.label.localeCompare(b.label));
		node.documents.sort((a, b) => a.title.localeCompare(b.title));
		for (const folder of node.folders) {
			sortTree(folder);
		}
	};
	sortTree(root);

	return root;
}

function KnowledgebaseTreeNode({
	node,
	openFolders,
	selectedRouteKey,
	onToggleFolder,
	onOpenFolder,
	onOpenDocument,
}: {
	node: KnowledgebaseFolderNode;
	openFolders: Set<string>;
	selectedRouteKey: string;
	onToggleFolder: (path: string) => void;
	onOpenFolder: (path: string) => void;
	onOpenDocument: (document: KnowledgebaseNavDocument) => void;
}) {
	const isRoot = node.path === "";
	const isOpen = isRoot || openFolders.has(node.path);
	const entries = React.useMemo(
		() => [
			...node.folders.map((folder) => ({ kind: "folder" as const, folder })),
			...node.documents.map((document) => ({
				kind: "document" as const,
				document,
			})),
		],
		[node.documents, node.folders],
	);

	if (!isOpen) return null;

	return (
		<div className={cn("space-y-0.5", !isRoot && "ml-3")}>
			{entries.map((entry, index) => {
				const isLast = index === entries.length - 1;
				if (entry.kind === "folder") {
					const folder = entry.folder;
					const folderOpen = openFolders.has(folder.path);
					const folderActive = selectedRouteKey === folder.path;
					const folderCount = folder.folders.length + folder.documents.length;

					return (
						<div key={folder.key} className="relative">
							<div
								className="absolute left-0 top-0 flex items-center"
								style={{ width: 16, height: "100%" }}
							>
								<div
									className="absolute left-[3px] w-px bg-sidebar-border/80"
									style={{
										top: isLast ? "0" : "-4px",
										height: isLast ? "50%" : "calc(100% + 8px)",
									}}
								/>
								<div
									className="absolute left-[3px] size-2 rounded-bl-md border-b border-l border-sidebar-border/80"
									style={{ top: "calc(50% - 4px)" }}
								/>
							</div>

							<div className="ml-4 space-y-0.5">
								<div
									className={cn(
										"flex items-center gap-2 rounded-xl border px-3 py-1.5 text-[0.82rem] transition-colors",
										folderActive
											? "border-border bg-background text-foreground"
											: "border-transparent text-muted-foreground hover:border-border hover:bg-background hover:text-foreground",
									)}
								>
									<button
										type="button"
										className="grid size-4 shrink-0 place-items-center"
										aria-label={folderOpen ? "Collapse folder" : "Expand folder"}
										onClick={() => onToggleFolder(folder.path)}
									>
										{folderOpen ? (
											<ChevronDown className="size-3.5" />
										) : (
											<ChevronRight className="size-3.5" />
										)}
									</button>
									<button
										type="button"
										className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
										onClick={() => onOpenFolder(folder.path)}
									>
										{folderOpen ? (
											<FolderOpen className="size-4 shrink-0" />
										) : (
											<Folder className="size-4 shrink-0" />
										)}
										<span className="truncate">{folder.label}</span>
										<span className="ml-auto text-[10px] tabular-nums text-muted-foreground">
											{folderCount}
										</span>
									</button>
								</div>

								{folderOpen ? (
									<KnowledgebaseTreeNode
										node={folder}
										openFolders={openFolders}
										selectedRouteKey={selectedRouteKey}
										onToggleFolder={onToggleFolder}
										onOpenFolder={onOpenFolder}
										onOpenDocument={onOpenDocument}
									/>
								) : null}
							</div>
						</div>
					);
				}

				const document = entry.document;
				const routeKey = buildKnowledgebaseRouteKey(document);
				const documentActive = routeKey === selectedRouteKey;

				return (
					<div key={document._id} className="relative flex items-center">
						<div
							className="absolute left-0 flex items-center"
							style={{ width: 16, height: "100%" }}
						>
							<div
								className="absolute left-[3px] w-px bg-sidebar-border/80"
								style={{
									top: isLast ? "0" : "-4px",
									height: isLast ? "50%" : "calc(100% + 8px)",
								}}
							/>
							<div
								className="absolute left-[3px] size-2 rounded-bl-md border-b border-l border-sidebar-border/80"
								style={{ top: "calc(50% - 4px)" }}
							/>
						</div>

						<button
							type="button"
							className={cn(
								"ml-4 flex w-full items-center gap-2.5 rounded-xl border px-3 py-1.5 text-left text-[0.82rem] transition-colors",
								documentActive
									? "border-border bg-background text-foreground"
									: "border-transparent text-muted-foreground hover:border-border hover:bg-background hover:text-foreground",
							)}
							onClick={() => onOpenDocument(document)}
						>
							<FileText className="size-4 shrink-0" />
							<span className="truncate">{document.title}</span>
						</button>
					</div>
				);
			})}
		</div>
	);
}

function KnowledgebaseSidebarTree({
	documents,
	selectedRouteKey,
	onNavigate,
}: {
	documents: KnowledgebaseNavDocument[];
	selectedRouteKey: string;
	onNavigate: (path: string) => void;
}) {
	const [openFolders, setOpenFolders] = React.useState<Set<string>>(new Set());

	const tree = React.useMemo(
		() => buildKnowledgebaseTree(documents),
		[documents],
	);

	const onToggleFolder = React.useCallback((path: string) => {
		setOpenFolders((previous) => {
			const next = new Set(previous);
			if (next.has(path)) next.delete(path);
			else next.add(path);
			return next;
		});
	}, []);

	const onOpenFolder = React.useCallback(
		(path: string) => {
			setOpenFolders((previous) => {
				if (!path || previous.has(path)) return previous;
				const next = new Set(previous);
				next.add(path);
				return next;
			});
			if (!path) {
				onNavigate(KNOWLEDGEBASE_BASE_PATH);
				return;
			}
			onNavigate(`${KNOWLEDGEBASE_BASE_PATH}/${encodeRouteKey(path)}`);
		},
		[onNavigate],
	);

	const onOpenDocument = React.useCallback(
		(document: KnowledgebaseNavDocument) => {
			const routeKey = buildKnowledgebaseRouteKey(document);
			onNavigate(`${KNOWLEDGEBASE_BASE_PATH}/${encodeRouteKey(routeKey)}`);
		},
		[onNavigate],
	);

	React.useEffect(() => {
		if (!selectedRouteKey) return;
		const segments = selectedRouteKey.split("/").filter(Boolean);
		if (segments.length === 0) return;

		setOpenFolders((previous) => {
			let changed = false;
			const next = new Set(previous);
			let current = "";
			for (const segment of segments.slice(0, -1)) {
				current = current ? `${current}/${segment}` : segment;
				if (!next.has(current)) {
					next.add(current);
					changed = true;
				}
			}
			if (
				documents.some(
					(document) =>
						normalizeFolderPath(document.folderPath) === selectedRouteKey,
				) &&
				!next.has(selectedRouteKey)
			) {
				next.add(selectedRouteKey);
				changed = true;
			}
			return changed ? next : previous;
		});
	}, [documents, selectedRouteKey]);

	return (
		<div className="ml-3 mt-0.5 max-h-[320px] overflow-auto">
			<KnowledgebaseTreeNode
				node={tree}
				openFolders={openFolders}
				selectedRouteKey={selectedRouteKey}
				onToggleFolder={onToggleFolder}
				onOpenFolder={onOpenFolder}
				onOpenDocument={onOpenDocument}
			/>
		</div>
	);
}

function SidebarLeaf({
	icon: Icon,
	active,
	label,
	onClick,
	className,
	needsReview,
}: {
	icon: IconComponent;
	active: boolean;
	label: string;
	onClick: () => void;
	className?: string;
	needsReview?: boolean;
}) {
	return (
		<button
			type="button"
			className={cn(
				"flex w-full items-center gap-2.5 rounded-xl border px-3 py-1.5 text-left text-sm font-medium transition-colors",
				active
					? "border-border bg-background text-foreground"
					: "border-transparent text-muted-foreground hover:border-border hover:bg-background hover:text-foreground",
				className,
				needsReview && "border-red-300 bg-red-50 text-red-700 hover:border-red-400 hover:bg-red-100 hover:text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-950/60 dark:hover:text-red-200",
			)}
			onClick={onClick}
		>
			<Icon className="size-4 shrink-0" />
			<span className="truncate">{label}</span>
			{needsReview ? <span className="ml-auto shrink-0 text-xs font-semibold" aria-label="Updates awaiting your review">Review</span> : null}
		</button>
	);
}

function SidebarItem({
	groupId,
	item,
	isActive,
	onNavigate,
	knowledgebaseDocuments,
	knowledgebaseSelectedRouteKey,
	knowledgebaseLoading,
	logisticsReviewStatus,
}: {
	groupId: NavGroupId;
	item: NavItem;
	isActive: (path: string) => boolean;
	onNavigate: (path: string) => void;
	knowledgebaseDocuments?: KnowledgebaseNavDocument[];
	knowledgebaseSelectedRouteKey?: string;
	knowledgebaseLoading?: boolean;
	logisticsReviewStatus?: LogisticsReviewStatus | null;
}) {
	const reviewSection = LOGISTICS_REVIEW_PATHS[item.path];
	const review = reviewSection ? logisticsReviewStatus?.[reviewSection] : undefined;
	const itemIcon = getNavItemIcon(item.path, groupId);
	const children = item.children ?? [];
	const hasActiveChild = children.some((child) => isActive(child.path));
	const itemActive = isActive(item.path) && !hasActiveChild;
	const showKnowledgebaseTree =
		groupId === "admin" && item.path === KNOWLEDGEBASE_BASE_PATH && (itemActive || hasActiveChild);

	return (
		<>
			<SidebarLeaf
				active={itemActive}
				needsReview={review?.needsReview}
				icon={itemIcon}
				label={item.label}
				onClick={() => onNavigate(item.path)}
			/>
			{itemActive && review?.needsReview && reviewSection ? (
				<LogisticsReviewControl section={reviewSection} revision={review.revision} label={item.label} />
			) : null}
			{children.length > 0 ? (
				<div className="ml-3 space-y-0.5">
					{children.map((child, index) => {
						const childActive = isActive(child.path);
						const childIcon = getNavItemIcon(child.path, groupId);
						const isLast = index === children.length - 1;

						return (
							<div key={child.path} className="relative flex items-center">
								<div
									className="absolute left-0 flex items-center"
									style={{ width: 16, height: "100%" }}
								>
									<div
										className="absolute left-[3px] w-px bg-sidebar-border/80"
										style={{
											top: isLast ? "0" : "-4px",
											height: isLast ? "50%" : "calc(100% + 8px)",
										}}
									/>
									<div
										className="absolute left-[3px] size-2 rounded-bl-md border-b border-l border-sidebar-border/80"
										style={{ top: "calc(50% - 4px)" }}
									/>
								</div>
								<SidebarLeaf
									active={childActive}
									className="ml-4 text-[0.82rem]"
									icon={childIcon}
									label={child.label}
									onClick={() => onNavigate(child.path)}
								/>
							</div>
						);
					})}
				</div>
			) : null}
			{showKnowledgebaseTree ? (
				knowledgebaseLoading ? (
					<div className="ml-7 mt-0.5 text-xs text-muted-foreground">
						Loading knowledgebase...
					</div>
				) : knowledgebaseDocuments && knowledgebaseDocuments.length > 0 ? (
					<KnowledgebaseSidebarTree
						documents={knowledgebaseDocuments}
						selectedRouteKey={knowledgebaseSelectedRouteKey ?? ""}
						onNavigate={onNavigate}
					/>
				) : (
					<div className="ml-7 mt-0.5 text-xs text-muted-foreground">
						No knowledgebase docs yet.
					</div>
				)
			) : null}
		</>
	);
}

function SidebarGroup({
	group,
	isActive,
	defaultOpen,
	onNavigate,
	badgeCount,
	badgeVariant,
	badgeClassName,
	knowledgebaseDocuments,
	knowledgebaseSelectedRouteKey,
	knowledgebaseLoading,
	logisticsReviewStatus,
}: {
	group: NavGroup;
	isActive: (path: string) => boolean;
	defaultOpen: boolean;
	onNavigate: (path: string) => void;
	badgeCount?: number;
	badgeVariant?: React.ComponentProps<typeof Badge>["variant"];
	badgeClassName?: string;
	knowledgebaseDocuments?: KnowledgebaseNavDocument[];
	knowledgebaseSelectedRouteKey?: string;
	knowledgebaseLoading?: boolean;
	logisticsReviewStatus?: LogisticsReviewStatus | null;
}) {
	const [open, setOpen] = React.useState(defaultOpen);
	const Icon = NAV_GROUP_ICONS[group.id];

	React.useEffect(() => {
		if (defaultOpen) {
			setOpen(true);
		}
	}, [defaultOpen]);

	return (
		<div className="space-y-0.5">
			<button
				type="button"
				aria-expanded={open}
				className={cn(
					"flex w-full items-center gap-2.5 rounded-xl border px-3 py-1.5 text-sm font-semibold transition-colors",
					open
						? "border-border bg-background text-foreground"
						: "border-transparent text-muted-foreground hover:border-border hover:bg-background hover:text-foreground",
				)}
				onClick={() => setOpen((state) => !state)}
			>
				<Icon className="size-4 shrink-0" />
				<span className="flex-1 truncate text-left">{group.label}</span>
				{typeof badgeCount === "number" && badgeCount > 0 ? (
					<Badge
						variant={badgeVariant ?? "secondary"}
						className={cn(
							"rounded-full px-2 py-0 text-[10px] tabular-nums",
							badgeClassName,
						)}
					>
						{badgeCount}
					</Badge>
				) : null}
				<ChevronRight
					className={cn(
						"size-4 transition-transform duration-200",
						open && "rotate-90",
					)}
				/>
			</button>

			{open ? (
				<div className="ml-4 mt-0.5 space-y-0.5 border-l border-sidebar-border/80 pl-2">
					{group.items.map((item) => (
						<SidebarItem
							key={item.path}
							groupId={group.id}
							isActive={isActive}
							item={item}
							onNavigate={onNavigate}
							knowledgebaseDocuments={knowledgebaseDocuments}
							knowledgebaseSelectedRouteKey={knowledgebaseSelectedRouteKey}
							knowledgebaseLoading={knowledgebaseLoading}
							logisticsReviewStatus={logisticsReviewStatus}
						/>
					))}
				</div>
			) : null}
		</div>
	);
}

export function AppSidebar({
	user,
	userRole,
	userEmail,
	collapsed,
	onNavigate,
}: AppSidebarProps) {
	useAppLocale();
	const { authUserId } = useAuthIdentity();
	const location = useLocation();
	const navigate = useNavigate();
	const setCurrentUserRoleInDev = useMutation(
		api.users.setCurrentUserRoleInDev,
	);
	const [isRoleSwitching, setIsRoleSwitching] = React.useState(false);
	const logisticsReviewStatus = useQuery(
		api.logisticsReview.getReviewStatus,
		authUserId && hasPermission(userRole, "logistics:edit") ? { authUserId } : "skip",
	) as LogisticsReviewStatus | null | undefined;
	const logisticsReviewCount = logisticsReviewStatus
		? Object.values(logisticsReviewStatus).filter((status) => status.needsReview).length
		: 0;
	const devRoleOptions = getDevRoleOptions();

	const supportInboxSummary = useQuery(
		api.support.getSupportInboxSummary,
		hasPermission(userRole, "internal:access") ? {} : "skip",
	) as
		| { openCount: number; handoffCount: number; needsReplyCount: number }
		| undefined;
	const supportNeedsReplyCount =
		typeof supportInboxSummary?.needsReplyCount === "number"
			? supportInboxSummary.needsReplyCount
			: 0;
	const supportHandoffCount =
		typeof supportInboxSummary?.handoffCount === "number"
			? supportInboxSummary.handoffCount
			: 0;
	const supportInboxBadgeCount =
		supportNeedsReplyCount > 0 ? supportNeedsReplyCount : supportHandoffCount;
	const supportInboxBadgeClassName =
		supportNeedsReplyCount > 0
			? BADGE_TONES.red
			: supportHandoffCount > 0
				? BADGE_TONES.amber
				: undefined;
	const salesFollowUpBadge = useQuery(
		(api as any).salesFollowUps.getNavBadge,
		authUserId && hasPermission(userRole, "sales:view") ? { authUserId } : "skip",
	) as { overdue: number; active: number } | undefined;
	const salesFollowUpBadgeCount =
		(salesFollowUpBadge?.overdue ?? 0) > 0
			? salesFollowUpBadge?.overdue ?? 0
			: salesFollowUpBadge?.active ?? 0;
	const salesFollowUpBadgeClassName =
		(salesFollowUpBadge?.overdue ?? 0) > 0 ? BADGE_TONES.red : BADGE_TONES.amber;

	const knowledgebaseListArgs = React.useMemo(() => {
		if (!authUserId || !hasPermission(userRole, "admin:view"))
			return "skip" as const;
		return {
			authUserId,
			includeContent: false,
			limit: 500,
		};
	}, [authUserId, userRole]);

	const knowledgebaseListResult = useQuery(
		api.knowledgeBase.listDocuments,
		knowledgebaseListArgs,
	);
	const knowledgebaseDocuments = React.useMemo(
		() =>
			(
				(knowledgebaseListResult?.documents ?? []) as KnowledgebaseNavDocument[]
			).filter((document) => document.status !== "archived"),
		[knowledgebaseListResult],
	);
	const knowledgebaseSelectedRouteKey = React.useMemo(
		() => decodeKnowledgebaseRouteKey(location.pathname),
		[location.pathname],
	);
	const knowledgebaseLoading =
		knowledgebaseListArgs !== "skip" && knowledgebaseListResult === undefined;

	const userInfo = user as { email?: string; name?: string } | undefined;
	const navGroups = getNavGroupsForRole(userRole, userEmail ?? userInfo?.email);

	const activeGroupId = React.useMemo(
		() => getActiveGroupId(location.pathname),
		[location.pathname],
	);

	const isActive = React.useCallback(
		(path: string) =>
			isNavPathActive(path, {
				pathname: location.pathname,
				search: location.search,
			}),
		[location.pathname, location.search],
	);

	const goTo = React.useCallback(
		(path: string) => {
			navigate(path);
			onNavigate?.();
		},
		[navigate, onNavigate],
	);

	const handleSignOut = React.useCallback(async () => {
		await authClient.signOut();
		window.location.assign("/login");
	}, []);

	const currentRoleOption = React.useMemo(
		() => devRoleOptions.find((option) => option.value === userRole),
		[devRoleOptions, userRole],
	);

	const currentRoleLabel =
		currentRoleOption?.label ?? userRole ?? m.status_unknown();

	const handleRoleSwitch = React.useCallback(
		async (nextRole: DevRoleValue) => {
			if (!ENV.IS_DEV || nextRole === userRole) return;
			setIsRoleSwitching(true);
			const nextRoleLabel =
				devRoleOptions.find((option) => option.value === nextRole)?.label ??
				nextRole;
			try {
				await setCurrentUserRoleInDev({ role: nextRole });
				toast.success(m.role_switched_to({ role: nextRoleLabel }));
			} catch (error) {
				const message =
					error instanceof Error ? error.message : m.role_switch_failed();
				toast.error(message);
			} finally {
				setIsRoleSwitching(false);
			}
		},
		[devRoleOptions, setCurrentUserRoleInDev, userRole],
	);

	if (collapsed) {
		return (
			<div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
				<div className="flex h-12 items-center justify-center border-b border-sidebar-border">
					<img
						src={anthemLogo}
						alt="Cherry"
						className="size-7 object-contain"
					/>
				</div>
				<nav className="flex-1 space-y-1 overflow-y-auto overscroll-contain p-1.5">
					{navGroups.map((group) => {
						const Icon = NAV_GROUP_ICONS[group.id];
						const active = activeGroupId === group.id;

						return (
							<button
								key={group.id}
								type="button"
								title={group.label}
								className={cn(
									"relative flex size-9 items-center justify-center rounded-xl border transition-colors",
									active
										? "border-border bg-background text-foreground"
										: "border-transparent text-muted-foreground hover:border-border hover:bg-background hover:text-foreground",
								)}
								onClick={() => goTo(group.basePath)}
							>
								<Icon className="size-4" />
								{group.id === "logistics" && logisticsReviewCount > 0 ? (
									<span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-red-500" role="img" aria-label="Logistics updates awaiting your review" />
								) : null}
								{group.id === "support" && supportInboxBadgeCount > 0 ? (
									<span
										className={cn(
											"absolute right-1.5 top-1.5 size-2 rounded-full",
											supportNeedsReplyCount > 0
												? "bg-red-500"
												: "bg-amber-500",
										)}
									/>
								) : null}
								{group.id === "sales" && salesFollowUpBadgeCount > 0 ? (
									<span
										className={cn(
											"absolute right-1.5 top-1.5 size-2 rounded-full",
											(salesFollowUpBadge?.overdue ?? 0) > 0
												? "bg-red-500"
												: "bg-amber-500",
										)}
									/>
								) : null}
							</button>
						);
					})}
				</nav>
				<div className="border-t border-sidebar-border p-1.5">
					{hasPermission(userRole, "internal:access") && (
						<div className="mb-1.5 flex justify-center">
							<button
								type="button"
								title="What’s new"
								aria-label="What’s new"
								aria-current={isActive("/changelog") ? "page" : undefined}
								onClick={() => goTo("/changelog")}
								className={cn(
									"flex size-10 items-center justify-center rounded-xl border transition-colors",
									isActive("/changelog")
										? "border-border bg-background text-foreground"
										: "border-transparent text-muted-foreground hover:border-border hover:bg-background hover:text-foreground",
								)}
							>
								<Rocket className="size-4" />
							</button>
						</div>
					)}
					<div className="mb-1.5 flex justify-center">
						<ThemeToggle />
					</div>
					<div className="mb-1.5 flex justify-center">
						<LanguageSwitcher collapsed />
					</div>
					{ENV.IS_DEV ? (
						<div className="mb-1.5 flex justify-center">
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<button
										type="button"
										title={m.current_role_as_value({ role: currentRoleLabel })}
										className="flex size-9 items-center justify-center rounded-xl border border-transparent text-muted-foreground transition-colors hover:border-border hover:bg-background hover:text-foreground disabled:opacity-50"
										disabled={isRoleSwitching}
									>
										<Settings className="size-4" />
									</button>
								</DropdownMenuTrigger>
								<DropdownMenuContent
									align="start"
									side="right"
									className="w-44"
								>
									{devRoleOptions.map((roleOption) => (
										<DropdownMenuItem
											key={roleOption.value}
											className="justify-between"
											disabled={isRoleSwitching}
											onClick={() => {
												void handleRoleSwitch(roleOption.value);
											}}
										>
											<span>{roleOption.label}</span>
											{roleOption.value === currentRoleOption?.value ? (
												<Check className="size-4" />
											) : null}
										</DropdownMenuItem>
									))}
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
					) : null}
					<button
						type="button"
						title={m.sign_out()}
						className="flex size-9 items-center justify-center rounded-xl border border-transparent text-muted-foreground transition-colors hover:border-border hover:bg-background hover:text-foreground"
						onClick={handleSignOut}
					>
						<LogOut className="size-4" />
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
			<div className="border-b border-sidebar-border px-4 py-3">
				<div className="flex items-center gap-3">
					<img
						src={anthemLogo}
						alt="Cherry"
						className="size-8 rounded-md object-contain"
					/>
					<div className="min-w-0">
						<p className="truncate text-[1.05rem] font-semibold tracking-tight">
							Cherry
						</p>
						<p className="truncate text-[11px] text-muted-foreground">
							by Anthem Coffee
						</p>
					</div>
					<div className="ml-auto">
						<ThemeToggle />
					</div>
				</div>
			</div>

			<nav className="flex-1 overflow-y-auto px-3 py-3">
				<p className="mb-1.5 px-2 text-xs font-semibold tracking-wide text-muted-foreground">
					{m.sidebar_navigation()}
				</p>
				<div className="space-y-0.5">
					{navGroups.map((group) => (
						<SidebarGroup
							key={group.id}
							defaultOpen={activeGroupId === group.id}
							logisticsReviewStatus={logisticsReviewStatus}
							badgeCount={
								group.id === "support"
									? supportInboxBadgeCount
									: group.id === "sales"
										? salesFollowUpBadgeCount
										: group.id === "logistics" ? logisticsReviewCount : undefined
							}
							badgeVariant={
								group.id === "support" || group.id === "sales" || group.id === "logistics"
									? "outline"
									: undefined
							}
							badgeClassName={
								group.id === "support"
									? supportInboxBadgeClassName
									: group.id === "sales"
										? salesFollowUpBadgeClassName
										: group.id === "logistics" ? BADGE_TONES.red : undefined
							}
							group={group}
							isActive={isActive}
							onNavigate={goTo}
							knowledgebaseDocuments={
								group.id === "admin" ? knowledgebaseDocuments : undefined
							}
							knowledgebaseSelectedRouteKey={
								group.id === "admin" ? knowledgebaseSelectedRouteKey : undefined
							}
							knowledgebaseLoading={
								group.id === "admin" ? knowledgebaseLoading : undefined
							}
						/>
					))}
				</div>
			</nav>

			<div className="border-t border-sidebar-border p-2.5">
				{hasPermission(userRole, "internal:access") && (
					<div className="mb-2">
						<SidebarLeaf
							icon={Rocket}
							label="What’s new"
							active={isActive("/changelog")}
							onClick={() => goTo("/changelog")}
						/>
					</div>
				)}
				{ENV.IS_DEV ? (
					<div className="mb-2">
						<p className="mb-1 px-1 text-[11px] font-medium text-muted-foreground">
							{m.current_role_as_label()}
						</p>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button
									type="button"
									className="flex w-full items-center gap-2 rounded-xl border border-transparent px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:border-border hover:bg-background hover:text-foreground disabled:opacity-50"
									disabled={isRoleSwitching}
								>
									<Settings className="size-4" />
									<span className="flex-1 text-left">{currentRoleLabel}</span>
									<Badge
										variant="outline"
										className="h-5 rounded-full px-2 text-[10px]"
									>
										DEV
									</Badge>
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="start" side="top" className="w-52">
								{devRoleOptions.map((roleOption) => (
									<DropdownMenuItem
										key={roleOption.value}
										className="justify-between"
										disabled={isRoleSwitching}
										onClick={() => {
											void handleRoleSwitch(roleOption.value);
										}}
									>
										<span>{roleOption.label}</span>
										{roleOption.value === currentRoleOption?.value ? (
											<Check className="size-4" />
										) : null}
									</DropdownMenuItem>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				) : null}
				<div className="mb-2">
					<LanguageSwitcher />
				</div>
				<button
					type="button"
					className="flex w-full items-center gap-2 rounded-xl border border-transparent px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:border-border hover:bg-background hover:text-foreground"
					onClick={handleSignOut}
				>
					<LogOut className="size-4" />
					<span>{m.sign_out()}</span>
				</button>
			</div>
		</div>
	);
}

export function SidebarTriggerButton({ onClick }: { onClick: () => void }) {
	return (
		<div className="-ml-1">
			<Button
				aria-label={m.sidebar_toggle()}
				variant="ghost"
				size="icon-sm"
				onClick={onClick}
			>
				<PanelLeft className="size-4" />
			</Button>
		</div>
	);
}
